import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  resource,
  signal,
  type Signal,
} from '@angular/core';
import { createLocatorFromSearchResult } from '../../helpers/locator';
import type { HighlightColor, SearchHighlight, TextFoundResponse } from '../../models/selections';
import { MetadataService } from '../metadata/metadata';
import { ReaderApi } from '../reader-api/reader-api';

@Injectable({
  providedIn: 'root',
})
export class TextSearchService {
  private readonly SEARCH_HIGHLIGHT_COLOR: HighlightColor = 'highlight-grey';
  private readonly readerApi = inject(ReaderApi);
  private readonly metadataService = inject(MetadataService);

  private readonly searchKeyword = signal<string>('');
  readonly searchHighlights = linkedSignal<SearchHighlight[]>(() => {
    const searchResults = this.searchResults.value();
    const searchText = this.wordToSearch();
    if (!searchResults) return [];
    return this.createSearchHighlights(searchResults, searchText);
  });
  readonly indexSearch = linkedSignal<number | undefined>(() => {
    if (this.searchHighlights().length) return 0;
    return undefined;
  });
  readonly searchPopoverOpened = linkedSignal(() => Boolean(this.searchKeyword().trim().length));
  readonly searchResults = resource({
    params: this.searchKeyword,
    loader: async ({ params: textToSearch }) => {
      if (!this.isSearchable(textToSearch)) return undefined;
      return this.readerApi.searchText(textToSearch);
    },
  });
  readonly wordToSearch: Signal<string> = this.searchKeyword.asReadonly();
  readonly minCharsToSearch = this.metadataService.minCharsToSearch;
  /** A search was asked for, but it's too short for the server to run it. */
  readonly isSearchTooShort = computed<boolean>(() => {
    const keyword = this.searchKeyword();
    return Boolean(keyword) && !this.isSearchable(keyword);
  });

  isSearchable(text: string): boolean {
    return text.trim().length >= this.minCharsToSearch();
  }

  search(text: string): void {
    this.searchKeyword.set(text.trim());
  }

  clearSearchResults(): void {
    this.searchKeyword.set('');
  }

  createSearchHighlights(results: TextFoundResponse[], searchText: string): SearchHighlight[] {
    return results.map((result) => ({
      id: crypto.randomUUID(),
      color: this.SEARCH_HIGHLIGHT_COLOR,
      selectedText: searchText,
      idRef: result.idRef,
      locator: createLocatorFromSearchResult(
        result,
        {
          positions: this.metadataService.positions(),
          componentNames: this.metadataService.componentNames(),
        },
        searchText,
      ),
      progress: result.position * 100,
      isBookmark: false,
    }));
  }
}
